import { useEffect, useRef, useState } from 'react'
import type { ChangeEvent } from 'react'
import { ImagePlus, Loader2 } from 'lucide-react'
import { cn } from '@/shared/ui/cn'
import { Button } from './button'

export type ProfilePhotoUploadProps = {
  value: string | null
  onChange: (url: string | null) => void
  onUpload: (file: File) => Promise<string>
  disabled?: boolean
  className?: string
}

export function ProfilePhotoUpload({
  value,
  onChange,
  onUpload,
  disabled,
  className,
}: ProfilePhotoUploadProps) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  const displayUrl = previewUrl ?? value

  const openFilePicker = () => {
    if (disabled || isUploading) return
    inputRef.current?.click()
  }

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    setErrorMessage('')
    setIsUploading(true)
    const objectUrl = URL.createObjectURL(file)
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev)
      return objectUrl
    })
    try {
      const url = await onUpload(file)
      onChange(url || objectUrl)
    } catch {
      setErrorMessage('Upload failed. Please try again.')
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev)
        return null
      })
    } finally {
      setIsUploading(false)
    }
  }

  const handleRemove = () => {
    if (disabled || isUploading) return
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev)
      return null
    })
    onChange(null)
  }

  return (
    <div className={cn('flex items-center gap-4', className)}>
      <button
        type="button"
        onClick={openFilePicker}
        disabled={disabled || isUploading}
        aria-label="Upload profile photo"
        className="relative flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-full border bg-muted text-muted-foreground transition-colors hover:bg-muted/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isUploading ? (
          <Loader2 className="size-6 animate-spin" />
        ) : displayUrl ? (
          <img
            src={displayUrl}
            alt="Profile preview"
            className="aspect-square size-full object-cover"
          />
        ) : (
          <ImagePlus className="size-6" />
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid="profile-photo-upload-input"
        onChange={handleFileChange}
      />
      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={openFilePicker}
            disabled={disabled || isUploading}
          >
            {displayUrl ? 'Change' : 'Upload'}
          </Button>
          {displayUrl ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleRemove}
              disabled={disabled || isUploading}
            >
              Remove
            </Button>
          ) : null}
        </div>
        {errorMessage ? (
          <p role="alert" className="text-xs text-destructive">
            {errorMessage}
          </p>
        ) : null}
      </div>
    </div>
  )
}
